import classnames from 'classnames'
import PropTypes from 'prop-types'
import {createRef, Component} from 'react'

import {setModalOpenClassOnBody} from '../../../lib/scrollTools'
import Closer from '../closer/Closer'
import {MODAL_CONTEXT_TYPES} from '../../ModalWrapper'
import {handleModalWrapperPosition} from '../../../lib/scrollTools'

const FOCUSABLE_SELECTORS = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"

export default class BaseModal extends Component {
  constructor(props) {
    super(props)

    this.contentsWrapperRef = createRef()
    this.wrapperPosition = handleModalWrapperPosition(props.wrapperContainerClass)
  }

  componentDidMount() {
    if (this.props.showModal || this.props.showModalOnMount) {
      this.wrapperPosition.lockWrapper()
      setModalOpenClassOnBody(true)

      // Actually open the modal for auto-opening modals:
      this.context.showModal && this.context.showModal() // Conditional is because modal may not always be used with modalWrapper. --BLR

      this.focusInitialElement()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.showModal !== nextProps.showModal) {
      setModalOpenClassOnBody(nextProps.showModal)

      if (nextProps.showModal)
        this.focusInitialElement()
    }
  }

  focusInitialElement = () => {
    const wrapper = this.contentsWrapperRef.current

    if (!wrapper) return

    wrapper.focus()
  }

  handleTrapFocus = event => {
    if (event.key !== 'Tab') return

    const wrapper = this.contentsWrapperRef.current

    if (!wrapper) return

    const focusable = Array.from(wrapper.querySelectorAll(FOCUSABLE_SELECTORS))
      .filter(element => element.offsetParent !== null)

    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && (active === first || active === wrapper)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  componentWillUnmount() {
    if (this.props.showModal) {
      setModalOpenClassOnBody(false)
      this.wrapperPosition.unlockWrapper()
    }
  }

  render() {
    const CloserComponent = this.props.closerComponent
    const contentsClassName = this.props.topLevelClassName ? `${this.props.topLevelClassName}-contents` : false

    return (
      <div className={classnames('core', 'base-modal', this.props.className)} ref='baseModal'>
        {this.props.showModal && <div className='base-modal-overlay' />}
        {
          this.props.showModal &&
          (
            <div
              className='base-modal-contents-wrapper'
              onKeyDown={this.handleTrapFocus}
              ref={this.contentsWrapperRef}
              role='dialog'
              aria-label={this.props.ariaLabel || 'Dialog'}
              aria-modal='true'
              tabIndex='-1'>
              <div className={classnames('base-modal-contents', contentsClassName)}>
                <div className='base-modal-heading-closer'>
                  {
                    this.props.showCloser && (
                      <CloserComponent
                        fill={this.props.closerFillColor}
                        alt='Close modal'
                        className='base-modal-closer'
                        closer={this.context.modalClosed}
                      />
                    )
                  }
                </div>
                {this.props.children}
              </div>
            </div>
          )
        }
      </div>
    )
  }
}

BaseModal.defaultProps = {
  closerComponent: Closer,
  showCloser: true,
  showModal: false,
  showModalOnMount: false,
  wrapperContainerClass: '.corp-max-controller'
}

BaseModal.propTypes = {
  ariaLabel: PropTypes.string,
  closerComponent: PropTypes.func,
  closerFillColor: PropTypes.string,
  showCloser: PropTypes.bool,
  showModal: PropTypes.bool,
  showModalOnMount: PropTypes.bool,
  topLevelClassName: PropTypes.string,
  wrapperContainerClass: PropTypes.string
}

BaseModal.contextTypes = MODAL_CONTEXT_TYPES
